import { useCallback, useMemo } from 'react';
import type { CSSProperties, KeyboardEvent } from 'react';
import type { IconName } from '@cbhq/cds-common';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import { Avatar } from '@cbhq/cds-web/media/Avatar';
import { TableCell, TableRow } from '@cbhq/cds-web/tables';
import { Text } from '@cbhq/cds-web/typography/Text';
import { useIntl } from '@cbhq/i18n';

import type { AuditLogRowEntry } from '../types';
import {
  formatAuditLogLocation,
  formatAuditLogTimestamp,
  getAuditActorName,
  getAuditEventMeta,
  getAuditEventTitle,
} from '../utils/auditLogDisplay';

export type AuditLogRowProps = {
  auditLog: AuditLogRowEntry;
  onAuditLogClick?: (eventId: string) => void;
  /** Delay (ms) before this row's reveal animation starts, used to stagger the batch */
  revealDelayMs?: number;
};

export const ROW_HEIGHT = 60;
// Actor absorbs the extra width so Action/Date/IP shift right and group together,
// while Date/IP stay narrow to keep them tight against each other.
export const AUDIT_LOG_ACTOR_CELL_WIDTH = '30%';
export const AUDIT_LOG_ACTION_CELL_WIDTH = '30%';
const EVENT_ICON_SLOT_WIDTH = 32;
export const AUDIT_LOG_DATE_CELL_WIDTH = '20%';
export const AUDIT_LOG_IP_ADDRESS_CELL_WIDTH = '20%';

const truncateStyle: CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const firstTableCellWhitespaceStyle = { paddingY: 0, paddingX: 0, paddingStart: 2 } as const;
const tableCellWhitespaceStyle = { paddingY: 0, paddingX: 3 } as const;

export function AuditLogRow({ auditLog, onAuditLogClick, revealDelayMs }: AuditLogRowProps) {
  const { formatMessage } = useIntl();
  const { actor, actorContext } = auditLog;

  const rowStyle = useMemo<CSSProperties>(
    () => ({
      height: ROW_HEIGHT,
      ...(onAuditLogClick ? { cursor: 'pointer' } : {}),
      ...(revealDelayMs ? { animationDelay: `${revealDelayMs}ms` } : {}),
    }),
    [onAuditLogClick, revealDelayMs],
  );

  const actorName = getAuditActorName(actor);
  const eventMeta = useMemo(() => getAuditEventMeta(auditLog.eventType), [auditLog.eventType]);
  const eventTitle = useMemo(
    () => getAuditEventTitle(auditLog.eventType, formatMessage),
    [auditLog.eventType, formatMessage],
  );
  const eventSubtitle = useMemo(() => formatAuditLogLocation(actorContext), [actorContext]);
  const timestampDisplay = useMemo(
    () => formatAuditLogTimestamp(auditLog.timestamp),
    [auditLog.timestamp],
  );
  const eventIconName = useMemo<IconName>(() => eventMeta?.iconName ?? 'info', [eventMeta]);
  const handleClick = useCallback(() => {
    onAuditLogClick?.(auditLog.eventId);
  }, [auditLog.eventId, onAuditLogClick]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTableRowElement>) => {
      if ((event.key === 'Enter' || event.key === ' ') && onAuditLogClick) {
        event.preventDefault();
        onAuditLogClick(auditLog.eventId);
      }
    },
    [auditLog.eventId, onAuditLogClick],
  );

  return (
    <TableRow
      className="workspaceRowReveal"
      style={rowStyle}
      data-testid="audit-log-row"
      data-dd-action-name={onAuditLogClick ? 'activity_audit_row_open' : undefined}
      tabIndex={onAuditLogClick ? 0 : undefined}
      onClick={onAuditLogClick ? handleClick : undefined}
      onKeyDown={onAuditLogClick ? handleKeyDown : undefined}
    >
      <TableCell
        outerSpacing={firstTableCellWhitespaceStyle}
        width={AUDIT_LOG_ACTOR_CELL_WIDTH}
        overflow="truncate"
      >
        <HStack alignItems="center" gap={2} width="100%">
          <Avatar
            colorScheme="gray"
            name={actorName}
            alt={actorName}
            src={actor.avatarUrl || undefined}
            shape="circle"
          />
          <VStack minWidth={0}>
            <Text as="span" font="label1" color="fg" style={truncateStyle}>
              {actorName}
            </Text>
            {actor.email ? (
              <Text as="span" font="label2" color="fgMuted" style={truncateStyle}>
                {actor.email}
              </Text>
            ) : null}
          </VStack>
        </HStack>
      </TableCell>

      <TableCell
        outerSpacing={tableCellWhitespaceStyle}
        width={AUDIT_LOG_ACTION_CELL_WIDTH}
        overflow="truncate"
      >
        <Box width="100%" minWidth={0}>
          <HStack alignItems="center" gap={2} width="100%">
            <Box width={EVENT_ICON_SLOT_WIDTH} justifyContent="flex-end">
              <Icon name={eventIconName} size="s" color="fg" />
            </Box>
            <VStack minWidth={0}>
              <Text as="span" font="label1" color="fg" style={truncateStyle}>
                {eventTitle}
              </Text>
              {eventSubtitle ? (
                <Text as="span" font="label2" color="fgMuted" style={truncateStyle}>
                  {eventSubtitle}
                </Text>
              ) : null}
            </VStack>
          </HStack>
        </Box>
      </TableCell>

      <TableCell
        outerSpacing={tableCellWhitespaceStyle}
        width={AUDIT_LOG_DATE_CELL_WIDTH}
        overflow="truncate"
      >
        <VStack minWidth={0}>
          <Text as="span" font="label2" color="fg" style={truncateStyle}>
            {timestampDisplay.date}
          </Text>
          <Text as="span" font="label2" color="fgMuted" style={truncateStyle}>
            {timestampDisplay.time}
          </Text>
        </VStack>
      </TableCell>

      <TableCell
        outerSpacing={tableCellWhitespaceStyle}
        width={AUDIT_LOG_IP_ADDRESS_CELL_WIDTH}
        overflow="truncate"
      >
        <Text as="span" font="label2" color="fgMuted" style={truncateStyle}>
          {actorContext.ipAddress}
        </Text>
      </TableCell>
    </TableRow>
  );
}
